/**
 * Typed error classes and response classifier for the login flow.
 * Intentionally framework-free so unit tests can import without React/jsdom.
 */

export class MasterLoginRequiredError extends Error {
  redirect: string;
  constructor(redirect: string) {
    super("Master admin sign-in required");
    this.redirect = redirect;
    this.name = "MasterLoginRequiredError";
  }
}

export class AccountLockedError extends Error {
  constructor() {
    super(
      "Account locked after too many failed attempts. Please try again in 15 minutes.",
    );
    this.name = "AccountLockedError";
  }
}

export class MfaCodeRequiredError extends Error {
  constructor() {
    super("MFA_CODE_REQUIRED");
    this.name = "MfaCodeRequiredError";
  }
}

export class MfaInvalidCodeError extends Error {
  constructor() {
    super("Invalid or expired code. Please try again.");
    this.name = "MfaInvalidCodeError";
  }
}

export class MfaSetupRequiredError extends Error {
  readonly redirect: string;
  constructor(redirect = "/settings/security") {
    super("MFA_SETUP_REQUIRED");
    this.name = "MfaSetupRequiredError";
    this.redirect = redirect;
  }
}

/**
 * Maps a non-OK login response body to the appropriate typed Error subclass.
 * Called from useLogin; exported separately so tests don't need a React env.
 */
export function parseLoginErrorBody(status: number, body: any): Error {
  if (body?.masterLoginRequired) {
    return new MasterLoginRequiredError(body.redirect || "/master/login");
  }
  if (body?.code === "ACCOUNT_LOCKED") return new AccountLockedError();
  if (body?.code === "MFA_CODE_REQUIRED") return new MfaCodeRequiredError();
  if (body?.code === "MFA_INVALID_CODE") return new MfaInvalidCodeError();
  if (body?.code === "MFA_SETUP_REQUIRED") {
    return new MfaSetupRequiredError(body.redirect || "/settings/security");
  }
  return new Error(body?.message || String(status));
}
